import { useEffect, useState } from 'react';
import { GripVertical, Loader2 } from 'lucide-react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import { api } from '@/utils/api';
import { formatINR } from '@/utils/formatters';
import type { WidgetDefinition } from './widgetRegistry';

/**
 * KPI payload shape returned by every /api/v1/analytics/** role-KPI endpoint.
 * Kept intentionally small — widgets decide which fields to render based on
 * their `variant`.
 */
interface KpiPayload {
  value: number | string | null;
  delta?: number;
  trend?: Array<{ t: string; v: number }>;
  breakdown?: Array<{ label: string; value: number }>;
  meta?: Record<string, unknown>;
}

interface Props {
  widget: WidgetDefinition;
  /** Scope pulled from roleLayouts — overrides widget default. */
  scope?: 'MY' | 'TEAM' | 'DEPT' | 'ORG';
}

const fmt = (v: number | string | null, format: WidgetDefinition['format']): string => {
  if (v == null) return '\u2014';
  const num = typeof v === 'string' ? parseFloat(v) : v;
  if (!Number.isFinite(num)) return String(v);
  const safe = Math.abs(num as number) > 1e15 ? 0 : (num as number);
  switch (format) {
    case 'currency':
      return formatINR(safe, false);
    case 'percentage':
      return `${Math.round(safe)}%`;
    case 'days':
      return `${Math.round(safe)}d`;
    case 'number':
      return safe.toLocaleString('en-IN');
    default:
      return String(safe);
  }
};

const PIE_COLORS = ['#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899'];

export function RoleKpiWidget({ widget, scope }: Props) {
  const [payload, setPayload] = useState<KpiPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!widget.endpoint) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setError(false);
        const sep = widget.endpoint!.includes('?') ? '&' : '?';
        const url = `${widget.endpoint}${scope ? `${sep}scope=${scope}` : ''}`;
        const res = await api.get(url);
        if (!cancelled) setPayload(res.data?.data ?? null);
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [widget.endpoint, scope]);

  const Icon = widget.icon;
  const variant = widget.variant ?? 'stat';

  const Header = () => (
    <div className="widget-drag-handle flex cursor-grab items-center justify-between border-b border-gray-100 px-3 py-2 active:cursor-grabbing">
      <div className="flex items-center gap-2 min-w-0">
        <div className={`flex h-7 w-7 flex-none items-center justify-center rounded-lg ${widget.color}`}>
          <Icon size={14} className={widget.iconColor} />
        </div>
        <span className="text-[11px] font-medium text-gray-500 leading-tight line-clamp-1">
          {widget.label}
        </span>
      </div>
      <GripVertical size={14} className="text-gray-300 flex-none" />
    </div>
  );

  const shell = (body: React.ReactNode) => (
    <div className="flex h-full flex-col rounded-xl border border-gray-200 bg-white shadow-sm transition-shadow hover:shadow-md">
      <Header />
      <div className="flex flex-1 min-h-0 flex-col px-3 py-2">{body}</div>
    </div>
  );

  if (loading) {
    return shell(
      <div className="flex h-full items-center justify-center">
        <Loader2 size={18} className="animate-spin text-gray-300" />
      </div>,
    );
  }
  if (error || !payload) {
    return shell(<div className="text-[11px] text-gray-400">No data yet</div>);
  }

  // ── Variant dispatcher ──────────────────────────────────────────────────
  switch (variant) {
    case 'list':
      return shell(
        <ul className="flex-1 overflow-auto space-y-1 text-[11px] text-gray-700">
          {(payload.breakdown ?? []).slice(0, 6).map((b, i) => (
            <li key={i} className="flex items-center justify-between gap-2">
              <span className="truncate">{b.label}</span>
              <span className="font-semibold text-gray-900 flex-none">
                {fmt(b.value, widget.format)}
              </span>
            </li>
          ))}
          {(!payload.breakdown || payload.breakdown.length === 0) && (
            <li className="text-gray-400">No entries</li>
          )}
        </ul>,
      );

    case 'bar':
      return shell(
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={payload.breakdown ?? []} margin={{ top: 4, right: 10, left: 0, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="label" tick={{ fontSize: 9 }} interval={0} angle={-15} height={40} />
            <YAxis tick={{ fontSize: 9 }} />
            <Tooltip />
            <Bar dataKey="value" fill="#3b82f6" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>,
      );

    case 'line':
      return shell(
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={payload.trend ?? []} margin={{ top: 4, right: 10, left: 0, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="t" tick={{ fontSize: 9 }} />
            <YAxis tick={{ fontSize: 9 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}K`} />
            <Tooltip />
            <Line dataKey="v" stroke="#22c55e" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>,
      );

    case 'pie':
      return shell(
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={payload.breakdown ?? []}
              dataKey="value"
              nameKey="label"
              innerRadius="40%"
              outerRadius="75%"
              paddingAngle={2}
            >
              {(payload.breakdown ?? []).map((_, i) => (
                <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
              ))}
            </Pie>
            <Tooltip />
            <Legend wrapperStyle={{ fontSize: 10 }} />
          </PieChart>
        </ResponsiveContainer>,
      );

    case 'stack':
      // Stacked aging chart — expects breakdown with ordered buckets.
      return shell(
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={(payload.breakdown ?? []).map((b) => ({ bucket: b.label, amount: b.value }))}
            margin={{ top: 4, right: 10, left: 0, bottom: 4 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="bucket" tick={{ fontSize: 9 }} />
            <YAxis tick={{ fontSize: 9 }} />
            <Tooltip formatter={(v: number) => formatINR(v, false)} />
            <Bar dataKey="amount" fill="#f59e0b" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>,
      );

    case 'map':
      // Minimal map placeholder — points live in meta.points. Real Leaflet
      // integration is a TODO; this widget ships as a styled list so the
      // demo still looks populated.
      const pts = ((payload.meta as any)?.points ?? []) as Array<{
        title?: string;
        lat: number;
        lng: number;
        at: string;
      }>;
      return shell(
        <div className="flex flex-col h-full">
          <div className="text-xs text-gray-500 mb-1">Recent check-ins</div>
          <ul className="flex-1 overflow-auto space-y-1 text-[11px]">
            {pts.length === 0 && <li className="text-gray-400">No GPS check-ins recorded</li>}
            {pts.slice(0, 8).map((p, i) => (
              <li key={i} className="flex items-center justify-between gap-2 border-b border-gray-50 py-1">
                <span className="truncate">{p.title ?? '(untitled)'}</span>
                <span className="font-mono text-[10px] text-gray-400 flex-none">
                  {p.lat.toFixed(3)}, {p.lng.toFixed(3)}
                </span>
              </li>
            ))}
          </ul>
        </div>,
      );

    case 'stat':
    default: {
      // Default: big number + optional delta + optional sparkline at the bottom
      return shell(
        <div className="flex flex-col h-full gap-1">
          <div className="flex items-baseline gap-2">
            <p
              className={`text-xl font-bold ${payload.value == null ? 'text-gray-300' : 'text-gray-900'}`}
            >
              {fmt(payload.value, widget.format)}
            </p>
            {typeof payload.delta === 'number' && (
              <span
                className={`text-[10px] font-semibold ${
                  payload.delta >= 0 ? 'text-green-600' : 'text-red-600'
                }`}
              >
                {payload.delta >= 0 ? '+' : ''}
                {payload.delta}%
              </span>
            )}
          </div>
          {Array.isArray(payload.trend) && payload.trend.length > 1 && (
            <div className="flex-1 min-h-0">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={payload.trend} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                  <Line dataKey="v" stroke="#3b82f6" strokeWidth={1.5} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
          {payload.breakdown && payload.breakdown.length > 0 && !payload.trend?.length && (
            <div className="text-[10px] text-gray-400 truncate">
              Top: {payload.breakdown[0]?.label}
            </div>
          )}
        </div>,
      );
    }
  }
}
